import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MainCard from 'ui-component/cards/MainCard';
import SkeletonBody from 'ui-component/skeleton/SkeletonBody';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';
import useAuth from 'hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'store';
import { startSnackbar } from 'store/slices/snackbar';
import UserGroup from 'types/userGroup';
import UserGroupService from 'services/UserGroupService';
import AssetService from 'services/AssetService';
import UserService from 'services/UserService';
import Asset from 'types/asset';
import User from 'types/user';

const userGroupService = new UserGroupService();
const assetService = new AssetService();
const userService = new UserService();

interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

const UserGroupList = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { user } = useAuth();
    const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));

    const [groups, setGroups] = useState<UserGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
    const [tabIndex, setTabIndex] = useState(0);

    const [allAssets, setAllAssets] = useState<Asset[]>([]);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [loadingAssets, setLoadingAssets] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const [addGroupOpen, setAddGroupOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [deleteConfirmGroup, setDeleteConfirmGroup] = useState<UserGroup | null>(null);

    const loadGroups = useCallback(async () => {
        if (!user?.tenantId) return;
        setLoading(true);
        try {
            const data = await userGroupService.getUserGroups(user.tenantId);
            setGroups(data ?? []);
        } catch {
            dispatch(startSnackbar(t('ERROR_LOADING_GROUPS'), 'error'));
        } finally {
            setLoading(false);
        }
    }, [user?.tenantId]);

    useEffect(() => {
        loadGroups();
    }, [loadGroups]);

    const loadAllAssets = useCallback(async () => {
        if (!user?.tenantId) return;
        setLoadingAssets(true);
        try {
            const data = await assetService.getAssets({
                page: 1,
                recordsPerPage: 1000,
                paired: 0,
                searchString: '',
                orderBy: 'name',
                orderAscending: true
            } as any);
            setAllAssets(data.items ?? []);
        } catch {
            /* ignore */
        } finally {
            setLoadingAssets(false);
        }
    }, [user?.tenantId]);

    const loadAllUsers = useCallback(async () => {
        if (!user?.tenantId) return;
        setLoadingUsers(true);
        try {
            const data = await userService.getUsers({
                page: 1,
                recordsPerPage: 1000,
                searchString: '',
                orderBy: 'name',
                orderAscending: true
            });
            setAllUsers(data.items ?? []);
        } catch {
            /* ignore */
        } finally {
            setLoadingUsers(false);
        }
    }, [user?.tenantId]);

    const openGroup = (group: UserGroup) => {
        setSelectedGroup(group);
        setTabIndex(0);
        setDrawerOpen(true);
        loadAllAssets();
        loadAllUsers();
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedGroup(null);
    };

    const handleAddGroup = async () => {
        if (!newGroupName.trim() || !user?.tenantId) return;
        try {
            await userGroupService.addUserGroup(newGroupName.trim(), user.tenantId);
            setNewGroupName('');
            setAddGroupOpen(false);
            await loadGroups();
            dispatch(startSnackbar(t('GROUP_ADDED'), 'success'));
        } catch {
            dispatch(startSnackbar(t('ERROR_ADDING_GROUP'), 'error'));
        }
    };

    const handleDeleteGroup = async (group: UserGroup) => {
        try {
            await userGroupService.deleteUserGroup(group.id);
            setDeleteConfirmGroup(null);
            await loadGroups();
            dispatch(startSnackbar(t('GROUP_DELETED'), 'success'));
        } catch {
            dispatch(startSnackbar(t('ERROR_DELETING_GROUP'), 'error'));
        }
    };

    const handleAssetToggle = async (asset: Asset) => {
        if (!selectedGroup) return;
        const existing = selectedGroup.assets.find((a) => a.assetId === asset.id);
        try {
            if (existing) {
                await userGroupService.removeAssetFromGroup(existing.assetUserGroupId);
            } else {
                await userGroupService.addAssetToGroup(asset.id, selectedGroup.id);
            }
            const updated = await userGroupService.getUserGroups(user!.tenantId);
            const refreshed = updated.find((g) => g.id === selectedGroup.id);
            if (refreshed) setSelectedGroup(refreshed);
            setGroups(updated ?? []);
        } catch {
            dispatch(startSnackbar(t('ERROR_UPDATING_GROUP'), 'error'));
        }
    };

    const handleUserToggle = async (member: User) => {
        if (!selectedGroup || !user?.tenantId) return;
        const existing = selectedGroup.users.find((u) => u.userId === member.id);
        try {
            if (existing) {
                await userGroupService.removeUserFromGroup(existing.userTenantUserGroupId);
            } else {
                await userGroupService.addUserToGroup(member.id, user.tenantId, selectedGroup.id);
            }
            const updated = await userGroupService.getUserGroups(user!.tenantId);
            const refreshed = updated.find((g) => g.id === selectedGroup.id);
            if (refreshed) setSelectedGroup(refreshed);
            setGroups(updated ?? []);
        } catch {
            dispatch(startSnackbar(t('ERROR_UPDATING_GROUP'), 'error'));
        }
    };

    const filteredGroups = groups.filter((g) =>
        g.name.toLowerCase().includes(searchText.toLowerCase())
    );

    const paginatedGroups = filteredGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <MainCard title={t('USER_GROUPS')} content={false}>
            {/* Toolbar */}
            <CardContent>
                <Grid container justifyContent="space-between" sx={{ mb: '1vh' }}>
                    <Grid item lg={12} xs={12} container>
                        <Grid lg={4} md={4} item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                                placeholder={t('SEARCH')}
                                value={searchText}
                            />
                        </Grid>
                        <Grid lg={4} md={4} item xs={2} zeroMinWidth />
                        <Grid
                            lg={4}
                            md={4}
                            xs={4}
                            item
                            sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
                        >
                            <Tooltip title={t('ADD_GROUP')}>
                                <IconButton
                                    className="addButton"
                                    sx={{ backgroundColor: theme.palette.primary.main }}
                                    size="small"
                                    onClick={() => setAddGroupOpen(true)}
                                >
                                    <AddIcon sx={{ color: '#fff', fontSize: '17px' }} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>

            {/* Table */}
            <TableContainer sx={{ pl: '2px', pr: '2px' }}>
                <Table
                    stickyHeader
                    sx={{
                        minWidth: 750,
                        '& .MuiTableCell-root': { padding: '2px 8px' }
                    }}
                    aria-labelledby="tableTitle"
                >
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">{t('NAME')}</TableCell>
                            <TableCell align="center">{t('ASSETS')}</TableCell>
                            <TableCell align="center">{t('USERS')}</TableCell>
                            <TableCell align="right" sx={{ paddingRight: '2%!important' }}>{t('ACTIONS')}</TableCell>
                        </TableRow>
                    </TableHead>
                    {!loading ? (
                        <TableBody>
                            {paginatedGroups.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">{t('NO_GROUPS')}</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedGroups.map((group) => (
                                    <TableRow key={group.id} hover tabIndex={-1}>
                                        <TableCell component="th" scope="row">
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                            >
                                                {group.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                icon={<InventoryIcon />}
                                                label={group.assets?.length ?? 0}
                                                size="small"
                                                color={group.assets?.length > 0 ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                icon={<PersonIcon />}
                                                label={group.users?.length ?? 0}
                                                size="small"
                                                color={group.users?.length > 0 ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ pr: 3, '& .MuiTableCell-root': { padding: '4px 8px' } }}>
                                            <Tooltip title={t('MANAGE_GROUP')}>
                                                <IconButton color="secondary" size="large" onClick={() => openGroup(group)}>
                                                    <GroupsIcon sx={{ fontSize: '1.3rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('DELETE')}>
                                                <IconButton
                                                    color="error"
                                                    size="large"
                                                    onClick={() => setDeleteConfirmGroup(group)}
                                                >
                                                    <DeleteIcon sx={{ fontSize: '1.3rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    ) : (
                        <SkeletonBody numHeaders={3} showAction />
                    )}
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={filteredGroups.length}
                rowsPerPage={rowsPerPage}
                page={page}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('OF')} ${count}`}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                labelRowsPerPage={t('ROWS_PER_PAGE')}
            />

            {/* Drawer: gestione gruppo */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={closeDrawer}
                PaperProps={{ sx: { width: mobileDevice ? '100%' : 520, p: 2 } }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h4">{selectedGroup?.name}</Typography>
                    <IconButton onClick={closeDrawer}><CloseIcon /></IconButton>
                </Stack>
                <Divider />

                <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mt: 1 }}>
                    <Tab label={`${t('ASSETS')} (${selectedGroup?.assets?.length ?? 0})`} />
                    <Tab label={`${t('USERS')} (${selectedGroup?.users?.length ?? 0})`} />
                </Tabs>

                {/* Tab Assets */}
                <TabPanel value={tabIndex} index={0}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        {t('SELECT_ASSETS_FOR_GROUP')}
                    </Typography>
                    {loadingAssets ? (
                        <Stack spacing={1}>{[1, 2, 3, 4].map((i) => (
                            <Box key={i} sx={{ height: 40, bgcolor: 'action.hover', borderRadius: 1 }} />
                        ))}</Stack>
                    ) : (
                        <List dense sx={{ maxHeight: 500, overflow: 'auto' }}>
                            {allAssets.map((asset) => {
                                const checked = !!selectedGroup?.assets.find((a) => a.assetId === asset.id);
                                return (
                                    <ListItem key={asset.id} disablePadding>
                                        <FormControlLabel
                                            sx={{ width: '100%', mx: 0 }}
                                            control={
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() => handleAssetToggle(asset)}
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <ListItemText
                                                    primary={asset.name}
                                                    secondary={asset.halogName ? `Device: ${asset.halogName}` : undefined}
                                                />
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                            {allAssets.length === 0 && (
                                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                    {t('NO_ASSETS')}
                                </Typography>
                            )}
                        </List>
                    )}
                </TabPanel>

                {/* Tab Users */}
                <TabPanel value={tabIndex} index={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        {t('SELECT_USERS_FOR_GROUP')}
                    </Typography>
                    {loadingUsers ? (
                        <Stack spacing={1}>{[1, 2, 3, 4].map((i) => (
                            <Box key={i} sx={{ height: 40, bgcolor: 'action.hover', borderRadius: 1 }} />
                        ))}</Stack>
                    ) : (
                        <List dense sx={{ maxHeight: 500, overflow: 'auto' }}>
                            {allUsers.map((member) => {
                                const checked = !!selectedGroup?.users.find((u) => u.userId === member.id);
                                return (
                                    <ListItem key={member.id} disablePadding>
                                        <FormControlLabel
                                            sx={{ width: '100%', mx: 0 }}
                                            control={
                                                <Checkbox
                                                    checked={checked}
                                                    onChange={() => handleUserToggle(member)}
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <ListItemText
                                                    primary={`${member.name} ${member.surname ?? ''}`}
                                                    secondary={member.username}
                                                />
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                            {allUsers.length === 0 && (
                                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                    {t('NO_USERS')}
                                </Typography>
                            )}
                        </List>
                    )}
                </TabPanel>
            </Drawer>

            {/* Dialog: aggiungi gruppo */}
            <Dialog open={addGroupOpen} onClose={() => setAddGroupOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('ADD_GROUP')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label={t('NAME')}
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddGroup(); }}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddGroupOpen(false)}>{t('CANCEL')}</Button>
                    <Button variant="contained" onClick={handleAddGroup} disabled={!newGroupName.trim()}>
                        {t('SAVE')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: conferma eliminazione */}
            <Dialog open={!!deleteConfirmGroup} onClose={() => setDeleteConfirmGroup(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('DELETE_GROUP')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('CONFIRM_DELETE_GROUP', { name: deleteConfirmGroup?.name })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmGroup(null)}>{t('CANCEL')}</Button>
                    <Button variant="contained" color="error" onClick={() => deleteConfirmGroup && handleDeleteGroup(deleteConfirmGroup)}>
                        {t('DELETE')}
                    </Button>
                </DialogActions>
            </Dialog>
        </MainCard>
    );
};

export default UserGroupList;
